import React, { useCallback, useEffect, useRef, useState } from 'react';

/*
 * ضبط یادداشت صوتی داخل مرورگر.
 *
 * نکته‌ی مهم: getUserMedia فقط در «زمینه‌ی امن» کار می‌کند — یعنی HTTPS یا
 * localhost. اگر سامانه روی http با آی‌پی سرو شود، مرورگر اجازه‌ی میکروفون
 * نمی‌دهد و اینجا پیام روشن نشان داده می‌شود به‌جای شکست خاموش.
 */

const MIME_CANDIDATES = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4'];

function pickMimeType() {
  if (typeof MediaRecorder === 'undefined') return null;
  return MIME_CANDIDATES.find((type) => MediaRecorder.isTypeSupported(type)) || '';
}

function formatClock(seconds) {
  const m = String(Math.floor(seconds / 60)).padStart(2, '0');
  const s = String(seconds % 60).padStart(2, '0');
  return `${m}:${s}`;
}

export default function VoiceRecorder({ onChange, maxSeconds = 180, disabled = false }) {
  const [state, setState] = useState('idle'); // idle | recording | ready
  const [seconds, setSeconds] = useState(0);
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);

  const secureContext =
    typeof window !== 'undefined' &&
    (window.isSecureContext || window.location.hostname === 'localhost');
  const supported =
    secureContext &&
    typeof navigator !== 'undefined' &&
    navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== 'undefined';

  const cleanupStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    clearInterval(timerRef.current);
  }, []);

  useEffect(() => () => {
    cleanupStream();
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [cleanupStream, previewUrl]);

  const stop = useCallback(() => {
    if (recorderRef.current?.state === 'recording') {
      recorderRef.current.stop();
    }
  }, []);

  const start = async () => {
    setError('');
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const mimeType = pickMimeType();
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      recorderRef.current = recorder;

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data);
      };

      recorder.onstop = () => {
        cleanupStream();
        const blob = new Blob(chunksRef.current, { type: mimeType || 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setPreviewUrl((old) => {
          if (old) URL.revokeObjectURL(old);
          return url;
        });
        setState('ready');
        onChange?.({ blob, seconds: secondsRef.current });
      };

      recorder.start();
      setState('recording');
      setSeconds(0);
      secondsRef.current = 0;
      timerRef.current = setInterval(() => {
        secondsRef.current += 1;
        setSeconds(secondsRef.current);
        if (secondsRef.current >= maxSeconds) stop();
      }, 1000);
    } catch (err) {
      cleanupStream();
      setError(
        err?.name === 'NotAllowedError'
          ? 'اجازه‌ی دسترسی به میکروفون داده نشد.'
          : 'میکروفون در دسترس نیست.'
      );
      setState('idle');
    }
  };

  const secondsRef = useRef(0);

  const discard = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setSeconds(0);
    secondsRef.current = 0;
    setState('idle');
    onChange?.(null);
  };

  if (!supported) {
    return (
      <div className="recorder recorder--unavailable">
        <span aria-hidden="true">🎙️</span>
        <p>
          ضبط صدا در این حالت ممکن نیست.
          {!secureContext && ' مرورگر فقط روی HTTPS یا localhost اجازه‌ی میکروفون می‌دهد.'}
          {' '}می‌توانید یادداشت متنی بنویسید.
        </p>
      </div>
    );
  }

  return (
    <div className="recorder">
      {state === 'idle' && (
        <button type="button" className="btn btn--ghost" onClick={start} disabled={disabled}>
          🎙️ ضبط یادداشت صوتی
        </button>
      )}

      {state === 'recording' && (
        <div className="recorder__live">
          <span className="recorder__pulse" aria-hidden="true" />
          <span className="num">{formatClock(seconds)}</span>
          <span className="recorder__hint">حداکثر {formatClock(maxSeconds)}</span>
          <button type="button" className="btn btn--primary btn--sm" onClick={stop}>
            توقف
          </button>
        </div>
      )}

      {state === 'ready' && previewUrl && (
        <div className="recorder__ready">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <audio controls src={previewUrl} className="recorder__audio" />
          <span className="recorder__meta num">{formatClock(seconds)}</span>
          <button type="button" className="link" onClick={discard}>
            حذف و ضبط دوباره
          </button>
        </div>
      )}

      {error && <p className="field__error">{error}</p>}
    </div>
  );
}
